import { useMemo, useRef, useState } from "react";
import { GenericInput } from "@asez/component-library";
import { STATIC_STRINGS } from "@asez/shared";
import AddIcon from "@mui/icons-material/Add";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import { GenericModal } from "@asez/component-library";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../../../store";
import {
  addCandidate,
  updateEmailBody,
} from "../../../../../store/steps/stepFiveSlice";
import { isEmpty } from "../../../../../utils";
import { SIZES } from "@asez/shared";
import { apiService } from "../../../services/apiService";
import { useParams } from "react-router-dom";
import { SpinnerButton } from "@asez/component-library";

const emailRegex = STATIC_STRINGS.EMAIL_REGEX;

const EmailInputCard = () => {
  const { assessmentId } = useParams();
  const dispatch = useDispatch();
  const emailBody = useSelector((state: RootState) => state.stepFive.emailBody);
  const [candidateName, setCandidateName] = useState<string>("");
  const [candidateEmail, setCandidateEmail] = useState<string>("");
  const [isEmailPreviewOpen, setEmailPreviewOpen] = useState("");
  const [localEmailBody, setLocalEmailBody] = useState(emailBody);
  const [loading, setLoading] = useState<boolean>(false);
  const emailTextareaRef = useRef<HTMLTextAreaElement>(null);

  const addEmailHandler = async () => {
    setLoading(true);
    try {
      await apiService.addEmails({
        id: assessmentId as string,
        emails: [{ email: candidateEmail, name: candidateName }],
      });
      dispatch(addCandidate({ name: candidateName, email: candidateEmail }));
      setCandidateName("");
      setCandidateEmail("");
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const isEmailValid = useMemo(
    () => emailRegex.test(candidateEmail),
    [candidateEmail]
  );
  const isButtonDisabled = useMemo(
    () => !isEmailValid || isEmpty(candidateName),
    [isEmailValid, candidateName]
  );

  return (
    <>
      <div className="w-1/2 mx-1 card rounded-lg h-full p-3 flex flex-col justify-between items-start border">
        <div className="font-semibold">{STATIC_STRINGS.ADD_EMAIL_MANUALLY}</div>
        <div className="flex flex-col xl:flex-row justify-center items-center h-1/2 w-full my-1">
          <div className="w-full xl:w-1/2">
            <div className="font-medium text-gray-700">
              {STATIC_STRINGS.CANDIDATE_NAME}
            </div>
            <GenericInput
              className="w-full"
              type="text"
              value={candidateName}
              onChange={(
                e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
              ) => {
                setCandidateName(e.target.value);
              }}
              placeholder={STATIC_STRINGS.NAME_PLACEHOLDER}
            />
          </div>
          <div className="w-full xl:ml-1 xl:w-1/2">
            <div className="font-medium text-gray-700">
              {STATIC_STRINGS.EMAIL}
            </div>
            <GenericInput
              className="w-full"
              type="email"
              value={candidateEmail}
              onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                setCandidateEmail(e.target.value);
              }}
              placeholder={STATIC_STRINGS.EMAIL_PLACEHOLDER}
            />
          </div>
        </div>
        <div className="flex flex-col h-1/3 mt-2 xl:flex-row justify-between items-end w-full">
          <SpinnerButton
            loading={loading}
            onClickHandler={addEmailHandler}
            className={`btn ${
              !isButtonDisabled
                ? `btn-primary border border-neutral-dark`
                : `btn-outline btn-outline-disabled pointer-events-none`
            } px-2 py-1 flex justify-center items-center shadow-md text-sm 2xl:text-base w-full xl:w-32`}
            size={SIZES.sm}
          >
            <AddIcon className="mr-1" fontSize={SIZES.sm} />{" "}
            {STATIC_STRINGS.ADD_EMAIL}
          </SpinnerButton>
          <button
            className="btn btn-outline px-2 py-1 flex justify-center items-center shadow-md text-sm 2xl:text-base w-full xl:w-fit btn-outline-disabled pointer-events-none"
            disabled={true}
            onClick={() => setEmailPreviewOpen("open")}
          >
            <MailOutlineIcon className="mr-1" fontSize={SIZES.sm} />
            {STATIC_STRINGS.PREVIEW_EMAIL}
          </button>
        </div>
      </div>
      <GenericModal
        onClose={() => setEmailPreviewOpen("")}
        onConfirm={() => {
          dispatch(updateEmailBody(localEmailBody));
          setEmailPreviewOpen("");
        }}
        open={isEmailPreviewOpen}
        title={STATIC_STRINGS.PREVIEW_EMAIL}
        body={
          <textarea
            className="w-full h-[40vh] p-1 border border-primary-light rounded-md focus:outline-none focus:ring-1 focus:ring-neutral-dark"
            ref={emailTextareaRef}
            value={localEmailBody}
            onChange={(e) => setLocalEmailBody(e.target.value)}
          />
        }
      />
    </>
  );
};

export default EmailInputCard;
